import { useEffect, useState, type FormEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { apiFetch } from "@/utils/api";

interface CustomerRow {
  id: string;
  name: string;
  description: string;
  poc: string;
  address: string;
}

const emptyForm = { name: "", description: "", poc: "", address: "" };

function CustomerFormFields({
  form,
  onChange,
}: {
  form: typeof emptyForm;
  onChange: (form: typeof emptyForm) => void;
}) {
  return (
    <>
      <div className="space-y-1.5">
        <Label>Name</Label>
        <Input
          value={form.name}
          onChange={(e) => onChange({ ...form, name: e.target.value })}
          required
        />
      </div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <Input
          value={form.description}
          onChange={(e) => onChange({ ...form, description: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label>POC</Label>
        <Input
          value={form.poc}
          onChange={(e) => onChange({ ...form, poc: e.target.value })}
          placeholder="Point of contact name or info"
        />
      </div>
      <div className="space-y-1.5">
        <Label>Address</Label>
        <Textarea
          value={form.address}
          onChange={(e) => onChange({ ...form, address: e.target.value })}
          rows={3}
          placeholder="Street, city, state, zip"
        />
      </div>
    </>
  );
}

export function AdminCustomersPage() {
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const data = await apiFetch<{ customers: CustomerRow[] }>("/customers");
    setCustomers(data.customers);
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setFormError("");
  };

  const startEdit = (customer: CustomerRow) => {
    setShowNew(false);
    setEditingId(customer.id);
    setForm({
      name: customer.name,
      description: customer.description,
      poc: customer.poc ?? "",
      address: customer.address ?? "",
    });
    setFormError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    resetForm();
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setFormError("");
    setSaving(true);
    try {
      await apiFetch("/customers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setShowNew(false);
      resetForm();
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    setFormError("");
    setSaving(true);
    try {
      await apiFetch(`/customers/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setEditingId(null);
      resetForm();
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Customers"
        actions={
          <Button
            onClick={() => {
              if (showNew) {
                setShowNew(false);
                resetForm();
              } else {
                cancelEdit();
                setShowNew(true);
              }
            }}
            className="bg-[#1e3a4f] hover:bg-[#163041]"
          >
            {showNew ? "Cancel" : "Add Customer"}
          </Button>
        }
      />
      {showNew && (
        <form
          onSubmit={(e) => void create(e)}
          className="bg-white rounded-lg shadow p-4 grid gap-3 max-w-md"
        >
          <CustomerFormFields form={form} onChange={setForm} />
          {formError && <p className="text-sm text-destructive">{formError}</p>}
          <Button type="submit" disabled={saving} className="w-fit bg-[#1e3a4f] hover:bg-[#163041]">
            {saving ? "Creating…" : "Create"}
          </Button>
        </form>
      )}
      {editingId && (
        <form
          onSubmit={(e) => void saveEdit(e)}
          className="bg-white rounded-lg shadow p-4 grid gap-3 max-w-md"
        >
          <h3 className="font-medium text-sm text-muted-foreground">Edit customer</h3>
          <CustomerFormFields form={form} onChange={setForm} />
          {formError && <p className="text-sm text-destructive">{formError}</p>}
          <div className="flex gap-2">
            <Button type="submit" disabled={saving} className="bg-[#1e3a4f] hover:bg-[#163041]">
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button type="button" variant="secondary" onClick={cancelEdit}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {!showNew && !editingId && (
        <DataTable
          columns={[
            { key: "name", header: "Name", render: (c) => c.name },
            { key: "desc", header: "Description", render: (c) => c.description || "—" },
            { key: "poc", header: "POC", render: (c) => c.poc || "—" },
            { key: "address", header: "Address", render: (c) => c.address || "—" },
            {
              key: "actions",
              header: "",
              render: (c) => (
                <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(c)}>
                  Edit
                </Button>
              ),
            },
          ]}
          rows={customers}
          rowKey={(c) => c.id}
        />
      )}
    </div>
  );
}
